import createClient from "openapi-fetch";

import { ApiError, NETWORK_ERROR, apiErrorFrom } from "@/shared/api/errors";
import type { components, paths } from "@/shared/api/schema";

export type Schemas = components["schemas"];
export type SessionData = Schemas["SessionOut"];

const REFRESH_PATH = "/api/v1/auth/refresh";
const AUTH_PREFIX = "/api/v1/auth/";

/* The access token lives in memory only (ADR-0005); the refresh token is an httpOnly cookie. */
let accessToken: string | null = null;
let refreshInFlight: Promise<SessionData | null> | null = null;
const sessionListeners = new Set<(session: SessionData | null) => void>();

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

/**
 * The current access token, for the rare request that cannot go through `api`: an upload that
 * reports its progress needs XMLHttpRequest, which fetch cannot replace.
 */
export function currentAccessToken(): string | null {
  return accessToken;
}

/** Notified when a background refresh succeeds or the session ends. */
export function onSessionChange(listener: (session: SessionData | null) => void): () => void {
  sessionListeners.add(listener);
  return () => sessionListeners.delete(listener);
}

function publish(session: SessionData | null): void {
  accessToken = session?.access_token ?? null;
  sessionListeners.forEach((listener) => {
    listener(session);
  });
}

/**
 * Start a session the server issued outside sign-in, such as accepting an invitation: keep its
 * token and tell the listeners (the session provider), exactly as a refresh does.
 */
export function startSession(session: SessionData): void {
  publish(session);
}

/** Exchange the refresh cookie for a new session. Concurrent callers share one request. */
export function refreshSession(): Promise<SessionData | null> {
  refreshInFlight ??= (async () => {
    try {
      const response = await fetch(REFRESH_PATH, { method: "POST", credentials: "same-origin" });
      const session = response.ok ? ((await response.json()) as { data: SessionData }).data : null;
      publish(session);
      return session;
    } catch {
      return null; // offline: keep the current state, callers decide what to show
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

function withAuthorization(request: Request): Request {
  if (accessToken !== null) {
    request.headers.set("Authorization", `Bearer ${accessToken}`);
  }
  return request;
}

/** Attach the token; on 401, refresh once and retry the original request. */
async function authorizedFetch(request: Request): Promise<Response> {
  const retry = request.clone();
  const response = await fetch(withAuthorization(request));
  const isAuthCall = new URL(request.url).pathname.startsWith(AUTH_PREFIX);
  if (response.status !== 401 || isAuthCall) {
    return response;
  }
  const session = await refreshSession();
  return session === null ? response : fetch(withAuthorization(retry));
}

export const api = createClient<paths>({
  baseUrl: window.location.origin,
  fetch: authorizedFetch,
  credentials: "same-origin",
});

interface FetchResult<T> {
  data?: { data: T };
  error?: unknown;
  response: Response;
}

/** Unwrap an openapi-fetch result into the envelope's data, or throw an ApiError. */
export async function unwrap<T>(request: Promise<FetchResult<T>>): Promise<T> {
  let result: FetchResult<T>;
  try {
    result = await request;
  } catch {
    throw new ApiError(NETWORK_ERROR, "Network request failed", 0);
  }
  if (result.data === undefined) {
    throw apiErrorFrom(result.error, result.response);
  }
  return result.data.data;
}
